export default class TierListForOverallHelper {
  /* Checks if a value is not null and not an empty string. */
  static validate(value) {
    return value !== null && value !== '';
  }

  /* Converts a value from a pricing detail's currency to opportunity currency.
     Uses the same formula as the SSRS Pricing Summary report: value * OpportunityCurrencyRate / CurrencyRate */
  static convertToOpportunityCurrency(value, pricingDetail) {
    return (
      (parseFloat(value) * parseFloat(pricingDetail.OpportunityCurrencyRate)) /
      parseFloat(pricingDetail.CurrencyRate)
    );
  }

  /* Converts a value from a tier's pricing detail currency to the current pricing detail's currency.
     Uses the same division-based formula as the SSRS report for consistency. */
  static convertToPricingCurrency(value, pricingDetail, pricingDetailForTier) {
    return (
      (parseFloat(value) * parseFloat(pricingDetail.CurrencyRate)) /
      parseFloat(pricingDetailForTier.CurrencyRate)
    );
  }

  /* Calculates the deferred cost per cycle for a tier.
     Matches the SQL view DeferredOtherCost which multiplies per-eval costs by CurrencyRate. */
  static calculateDeferredCostPerCycle(tier) {
    let deferredCostPerCycle = 0;
    const serviceTypeCode = tier.VPricingDetailBaseItem.ServiceTypeCode;
    const statusCode = tier.VPricingDetailBaseItem.StatusCode;
    const numEvalsPerCycle = tier.NumEvalsPerCycle;
    const numCyclesPerYr = tier.VPricingDetailBaseItem.NumCyclesPerYr;
    const currencyRate = tier.VPricingDetailBaseItem.CurrencyRate;

    // Editor/Trans/ExtDataCheck costs — SQL DeferredOtherCost multiplies these by CurrencyRate
    const deferredEditorCostPerCycle = tier.VPricingDetailBaseItem.EditorCostPerEval * numEvalsPerCycle * currencyRate;
    const deferredTransCostPerCycle = tier.VPricingDetailBaseItem.TransCostPerEval * numEvalsPerCycle * currencyRate;
    const deferredExtDataCheckPerCycle = tier.VPricingDetailBaseItem.ExtDataCheckPerEval * numEvalsPerCycle * currencyRate;

    // Commission — uses MarkedUpFeePerCycle directly (not multiplied by CurrencyRate, same as SQL)
    const deferredCommissionPctConv = tier.VPricingDetailBaseItem.CommissionPct / 100;
    const deferredMarkedUpRevenuePerCycleTotal = parseFloat(tier.MarkedUpFeePerCycle);
    const deferredCommissionPerCycle = parseFloat(deferredMarkedUpRevenuePerCycleTotal * deferredCommissionPctConv);

    // Vocazilla — SQL DeferredOtherCost multiplies by CurrencyRate
    const deferredVocazillaCostPerCycle = parseFloat(
      tier.VPricingDetailBaseItem.VocazillaCostPerEval * numEvalsPerCycle * currencyRate
    );
    // IVR — SQL DeferredOtherCost multiplies by CurrencyRate
    const deferredIvrFeeCostPerCycle =
      parseFloat(tier.VPricingDetailBaseItem.IvrfeeCostPerEval * numEvalsPerCycle * currencyRate) || 0;
    const deferredIvrFeeCostAnnually = parseFloat(deferredIvrFeeCostPerCycle * numCyclesPerYr);
    // ICR — SQL DeferredOtherCost multiplies by CurrencyRate
    const deferredIcrFeeCostPerCycle =
      parseFloat(tier.VPricingDetailBaseItem.IcrfeeCostPerEval * numEvalsPerCycle * currencyRate) || 0;
    // SM — SQL DeferredOtherCost multiplies by CurrencyRate
    const deferredSmCostPerEval =
      tier.VPricingDetailBaseItem.SMCostPerEvalOverride === true
        ? tier.VPricingDetailBaseItem.SMCostPerEval
        : tier.VPricingDetailBaseItem.SMCostPerEvalOrig;
    const deferredSmCostPerCycle = parseFloat(deferredSmCostPerEval * numEvalsPerCycle * currencyRate);

    // Owning office editing
    if (tier.VPricingDetailBaseItem.OwningOfficeIsEditing === true) {
      deferredCostPerCycle += deferredEditorCostPerCycle;
    }
    // Owning office translating
    if (tier.VPricingDetailBaseItem.OwningOfficeIsTranslating === true) {
      deferredCostPerCycle += deferredTransCostPerCycle;
    }
    // Owning office checking
    if (tier.VPricingDetailBaseItem.OwningOfficeIsChecking === true) {
      deferredCostPerCycle += deferredExtDataCheckPerCycle;
    }
    // Commission always
    deferredCostPerCycle += deferredCommissionPerCycle;
    // ServiceTypeCode logic
    if (statusCode === 2 || [1, 2, 3, 6, 9].includes(serviceTypeCode)) {
      deferredCostPerCycle += deferredVocazillaCostPerCycle;
    }
    if (statusCode === 2 || serviceTypeCode === 10) {
      deferredCostPerCycle += deferredIvrFeeCostAnnually;
    }
    if (statusCode === 2 || serviceTypeCode === 7) {
      deferredCostPerCycle += deferredIcrFeeCostPerCycle;
    }
    if (serviceTypeCode !== 7 && serviceTypeCode !== 10) {
      deferredCostPerCycle += deferredSmCostPerCycle;
    }
    return deferredCostPerCycle;
  }
}
